// @ts-nocheck
/**
 * Weekly Trend Chart Component
 * Carbon Charts implementation for weekly learning trends visualization
 */

import React, { useMemo } from 'react';
import { AreaChart } from '@carbon/charts-react';
import { Tile, Loading, InlineNotification } from '@carbon/react';
import { Analytics } from '@carbon/icons-react';
import '@carbon/charts-react/styles.css';

interface WeeklyTrendData {
  week: string;
  learningHours: number;
  completionRate: number;
  category?: string;
}

interface WeeklyTrendChartProps {
  data: WeeklyTrendData[];
  height?: number;
  title?: string;
  loading?: boolean;
  error?: string;
}

const WeeklyTrendChart: React.FC<WeeklyTrendChartProps> = ({
  data,
  height = 280,
  title = 'Weekly Learning Trends',
  loading = false,
  error = '',
}) => {
  // Transform data for Carbon Charts format
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map(item => ({
      date: item.week,
      group: 'Learning Hours',
      value: item.learningHours,
      completionRate: item.completionRate,
    }));
  }, [data]);

  // Carbon Charts options with Sigma08 theme colors
  const chartOptions = useMemo(
    () => ({
      title: '',
      axes: {
        left: {
          title: 'Learning Hours',
          mapsTo: 'value',
        },
        bottom: {
          title: 'Week',
          mapsTo: 'date',
          scaleType: 'labels' as const,
        },
      },
      curve: 'curveMonotoneX' as const,
      height,
      theme: 'white',
      color: {
        scale: {
          'Learning Hours': '#8a3ffc', // Galaxy Purple
        },
      },
      grid: {
        x: {
          enabled: false,
        },
        y: {
          enabled: true,
        },
      },
      points: {
        enabled: true,
        radius: 4,
        fillOpacity: 1,
        filled: true,
      },
      tooltip: {
        enabled: true,
        customHTML: (datapoints: any[]) => {
          if (!datapoints || datapoints.length === 0) return '';
          const point = datapoints[0];
          return `
          <div class="bx--cc--tooltip">
            <p class="bx--cc--tooltip-total">Week of ${point.date}</p>
            <ul class="bx--cc--tooltip-data">
              <li>
                <span class="bx--cc--tooltip-data__label">Learning Hours:</span>
                <span class="bx--cc--tooltip-data__value">${point.value?.toFixed(1)} hrs</span>
              </li>
              ${
                point.completionRate
                  ? `
              <li>
                <span class="bx--cc--tooltip-data__label">Completion Rate:</span>
                <span class="bx--cc--tooltip-data__value">${point.completionRate}%</span>
              </li>`
                  : ''
              }
            </ul>
          </div>
        `;
        },
      },
      legend: {
        enabled: false,
      },
      animations: {
        enabled: true,
      },
      fillOpacity: 0.3,
      timeScale: {
        addSpaceOnEdges: 1,
      },
    }),
    [height]
  );

  if (loading) {
    return (
      <Tile>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: height + 40,
            gap: 'var(--cds-spacing-03)',
          }}
        >
          <Loading withOverlay={false} />
          <span style={{ color: 'var(--cds-text-secondary)' }}>Loading chart data...</span>
        </div>
      </Tile>
    );
  }

  if (error) {
    return (
      <Tile>
        <InlineNotification title="Chart Error" subtitle={error} kind="error" lowContrast hideCloseButton />
      </Tile>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Tile>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: height + 40,
            color: 'var(--cds-text-secondary)',
          }}
        >
          <Analytics size={32} style={{ marginBottom: 'var(--cds-spacing-03)' }} />
          <span>No data available for the selected period</span>
        </div>
      </Tile>
    );
  }

  return (
    <Tile>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--cds-spacing-03)',
          marginBottom: 'var(--cds-spacing-05)',
        }}
      >
        <Analytics size={20} style={{ color: 'var(--cds-interactive-01)' }} />
        <h4
          style={{
            fontSize: 'var(--cds-productive-heading-03-font-size)',
            fontWeight: 'var(--cds-productive-heading-03-font-weight)',
            lineHeight: 'var(--cds-productive-heading-03-line-height)',
            letterSpacing: 'var(--cds-productive-heading-03-letter-spacing)',
            margin: 0,
          }}
        >
          {title}
        </h4>
      </div>

      <div
        style={{
          backgroundColor: 'var(--cds-layer-01)',
          borderRadius: 'var(--cds-border-radius)',
          padding: 'var(--cds-spacing-03)',
        }}
      >
        <AreaChart data={chartData} options={chartOptions} />
      </div>
    </Tile>
  );
};

export default WeeklyTrendChart;
